<template>
    <div
        class="event"
        :class="[{ loading }, { isActive }, { isOver }]"
        @click="$emit('click')"
        @mouseleave="contextMenuOpen = false"
        @mouseenter="contextMenuOpen = true"
    >
        <div v-if="loading" class="loading-skeleton">
            <SvgLoader path="assets/img/skeletons/event-skeleton" />
        </div>
        <div v-else class="main-content">
            <div class="event-image" :style="`background-image: url(${data.coverImageUrl});`"></div>
            <div class="event-title">
                <div class="title copy-l bold" :title="data.name">{{ data.name }}</div>
                <div class="time copy-m">{{ startTime }}</div>
            </div>
            <div class="col event-genres">
                <Tag v-for="(genre, index) in data.genres" :key="index" :value="genre" />
            </div>
            <div class="col event-rsvp">
                <div class="rsvp-container">
                    <FeatherIcon class="user-icon" name="users"></FeatherIcon>
                    <div class="text copy-m">
                        <span>{{ data.sourceAttendingCount }} {{ $t('event.attending') }} </span
                        ><span>{{ data.sourceInterestedCount }} {{ $t('event.are_interested') }}</span>
                    </div>
                </div>
            </div>
            <div class="event-more">
                <DContextMenu>
                    <DContextMenuItem icon="edit-3" :label="$t('event.edit')" type="default" @click="$emit('click')" />
                    <DContextMenuItem
                        icon="eye"
                        :label="$t('general.publish')"
                        type="default"
                        @click="$emit('publish', data)"
                    />
                    <DContextMenuItem
                        icon="eye-off"
                        :label="$t('general.unpublish')"
                        type="default"
                        @click="$emit('unpublish', data)"
                    />
                    <DContextMenuItem
                        icon="x"
                        :label="$t('event.cancel')"
                        type="default"
                        @click="$emit('cancelEvent', data)"
                    />
                    <DContextMenuItem
                        icon="trash-2"
                        :label="$t('general.delete')"
                        type="warning"
                        @click="$emit('delete', data)"
                    />
                </DContextMenu>
            </div>
        </div>
    </div>
</template>

<script>
import Tag from '@/components/Tag'
import FeatherIcon from '@/components/FeatherIcon'
import SvgLoader from '@/components/SvgLoader'
import DContextMenu from '@/components/DContextMenu'
import DContextMenuItem from '@/components/DContextMenuItem'

export default {
    name: 'Event',
    components: {
        Tag,
        FeatherIcon,
        SvgLoader,
        DContextMenu,
        DContextMenuItem,
    },
    props: {
        data: {
            type: Object,
            default: undefined,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            contextMenuOpen: false,
        }
    },
    computed: {
        startTime() {
            if (!this.data) return null

            let time = new Date(this.data.startDate)
            const options = { day: 'numeric', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
            return time.toLocaleDateString('de-DE', options) + ' ' + this.$t('general.hour')
        },
        isActive() {
            if (!this.data) return null

            let startDate = new Date(this.data.startDate).getTime()
            let endDate = new Date(this.data.endDate).getTime()
            let currentTime = Date.now()

            return currentTime > startDate && currentTime < endDate
        },
        isOver() {
            if (!this.data) return null
            if (!this.data.endDate) return null

            let endDate = new Date(this.data.endDate).getTime()
            let currentTime = Date.now()

            return currentTime > endDate
        },
    },
}
</script>

<style scoped lang="sass">
.event
    box-sizing: border-box
    cursor: pointer
    width: 100%
    max-width: 100%

    &.isActive
        .time
            color: $primary-2 !important
            font-weight: bold

    &.isOver
        opacity: 0.5

    &.loading
        pointer-events: none

    &:hover
        .main-content
            background: white
            box-shadow: $box-mois

        .event-more
            opacity: 1 !important

        .event-rsvp
            transform: translate(0px, 0) !important

    .main-content
        padding: $m $l
        display: grid
        grid-gap: 12px
        border-radius: 1rem
        align-items: center
        grid-template-columns: 48px 237px 1fr 1fr 32px
        grid-template-areas: "logo title genres rsvp more"

        @include breakpoint('m')
            background-color: $neutral-white
            grid-template-columns: 48px auto 32px
            margin-bottom: 12px
            grid-template-areas: "logo title more" "rsvp rsvp rsvp" "genres genres genres"
            box-shadow: $box-mois

        .event-image
            grid-area: logo

            width: 100%
            height: 100%

            min-width: 48px
            max-height: 48px
            background: $gray-1
                size: cover
                position: center
            border-radius: 8px

        .event-title
            grid-area: title

            .time
                color: $neutral-2

        .event-genres
            grid-area: genres

            white-space: nowrap
            text-overflow: ellipsis
            overflow: hidden
            color: transparent

            .tag
                display: inline-flex
                flex-wrap: wrap

        .event-rsvp
            grid-area: rsvp

            display: flex
            align-items: center

            .rsvp-container
                display: flex
                align-items: center
                justify-content: flex-end

                .text
                    white-space: nowrap
                    text-align: right
                    @include breakpoint('m')
                        display: flex
                        flex-wrap: wrap

                .feather-icon.user-icon
                    width: 16px
                    margin-right: 4px

        .event-more
            grid-area: more

            width: 3rem
            height: 3rem
            background: $gray-1
            display: flex
            align-items: center
            justify-content: center
            border-radius: 99em
            margin-left: $m
            opacity: 0
            z-index: 20
            +transition(80ms)
            @include breakpoint('m')
                opacity: 1
                align-self: flex-start
                margin-left: 0

            .feather-icon
                width: 1.8rem
</style>
